import CustomerPortalGraphic from '@ar/assets/images/customer-portal-graphic.jpg';
import { Button, mergeClassNames, Modal, MultiSelectDropdown, toast, Typography } from '@notch-ordering/ui-components';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { sendPortalInvite } from '@/ar/network/AccountsReceivable.network';
import { useEmailOptions } from '@/ar/hooks/useEmailOptions';
import { FETCH_CUSTOMER_QUERY_KEY, useGetCustomer } from '@/ar/hooks/queries/CustomerQueries.hook';
import { drawerInsideClassName } from '../../CustomerDetails/CustomerGeneralPopup/CustomerGeneralPopup';
import { emailPatternSource } from '../modals/CreateAdyenPaymentMethod/CreateBankAccount/MandateModal/MandateModal';
import { queryClient } from '@/containers/app/Root';

export interface Props {
    customerID: string,
    supplierID: string,
    gpoId: string,
    isOpen: boolean,
    onClose: () => void,
    title?: React.ReactNode,
    description?: React.ReactNode,
    returnPath?: string,
    successMessage?: (emails: string[]) => string,
    errorMessage?: string,
}

const defaultDescription = <Typography className="my-0">Share a secure portal link with your customer so they can view, manage payment methods, and pay any invoice. Less follow-up for you, more convenience for them.</Typography>;

export const PortalInviteModal: React.FC<Props> = ({
    customerID,
    supplierID,
    gpoId,
    isOpen,
    onClose,
    title = 'Share customer portal',
    description = defaultDescription,
    returnPath,
    successMessage = (emails: string[]) => `Portal invite${emails.length > 1 ? 's' : ''} sent successfully`,
    errorMessage = 'There was an error sending the portal invite. Please try again.',
}) => {
    const { data: customer } = useGetCustomer({
        customerID,
        supplierID,
        gpoId,
    });

    const { allOptions, defaultOptions, selectedEmails, setSelectedEmails } = useEmailOptions(customer);

    const sendPortalInviteMutation = useMutation(sendPortalInvite);

    const handleClose = () => {
        onClose();
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (selectedEmails.length > 0) {
            // Create an array of promises for all email sends
            const sendPortalPromises = selectedEmails.map((email) => sendPortalInviteMutation.mutateAsync({
                body: {
                    customer_ids: [customerID],
                    email,
                    supplier_id: supplierID,
                    return_path: returnPath,
                },
                customerID,
                supplierID,
                gpoId,
            }));

            Promise.all(sendPortalPromises)
                .then(() => {
                    toast.show({
                        message: successMessage(selectedEmails),
                    });
                    queryClient.invalidateQueries([FETCH_CUSTOMER_QUERY_KEY]);
                    onClose();
                })
                .catch(() => {
                    toast.show({
                        message: errorMessage,
                    });
                });
        }
    };

    if (!customer) {
        return null;
    }

    return (
        <Modal
            className={drawerInsideClassName}
            isOpen={isOpen}
            onClose={handleClose}
            titleSeparatorDesktop={true}
            desktopModalWidth={mergeClassNames('lg:w-[800px] transition-all')}
            title={title}
            close={handleClose}>

            <form onSubmit={onSubmit}>
                <div>
                    <div className="mt-8 mb-2">
                        <div className="mx-8">
                            <div className="flex gap-6 lg:flex-row flex-col">
                                <div className="lg:w-6/12 w-full">
                                    <img src={CustomerPortalGraphic} alt="Customer Portal Graphic" />
                                </div>
                                <div className="lg:w-6/12 w-full space-y-6 relative">
                                    {description}
                                    <MultiSelectDropdown
                                        defaultValues={defaultOptions}
                                        editPattern={emailPatternSource}
                                        onChange={(choseOptions) => setSelectedEmails(choseOptions.map((option) => option.value))}
                                        notCorrectNewValueMessage="(not correct value yet)"
                                        options={allOptions}/>
                                    <Button
                                        disabled={sendPortalInviteMutation.isLoading || selectedEmails.length === 0}
                                        variant="SECONDARY"
                                        size="MEDIUM"
                                        minWidth="w-auto"
                                        type="submit"
                                        className="flex items-center"
                                        loading={sendPortalInviteMutation.isLoading}>
                                        Send
                                    </Button>
                                    <div className="w-full h-px bg-gray-200 absolute bottom-0" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </form>
        </Modal>
    );
};
